//app/frontend/components/AddressAutocompleteMap.tsx
import React, { useRef, useEffect, useState } from 'react';
import { TextInput, Select } from '@mantine/core';
import { Autocomplete } from '@react-google-maps/api';

interface TimezoneOption {
  value: string;
  label: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onTimezoneSelect: (timeZoneId: string) => void;
  timezoneValue: string;
  // Optional: you can pass additional props if needed (e.g., required, styles)
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onTimezoneSelect,
  timezoneValue,
}) => {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [timezoneOptions, setTimezoneOptions] = useState<TimezoneOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch list of timezones on component mount
  useEffect(() => {
    fetchTimezoneList();
  }, []);

  const fetchTimezoneList = async () => {
    try {
      const apiKey = 'N2IE9BKU2EQR'; // Your TimeZoneDB API key
      const response = await fetch(`https://api.timezonedb.com/v2.1/list-time-zone?key=${apiKey}&format=json`);
      const data = await response.json();

      if (data.status === 'OK' && data.zones) {
        const options = data.zones.map((zone: any) => ({
          value: zone.zoneName,
          label: `${zone.zoneName} (${formatGMTOffset(zone.gmtOffset)})`,
        }));
        setTimezoneOptions(options);
      } else {
        console.error('Error fetching timezone list:', data.message);
      }
    } catch (error) {
      console.error('Error fetching timezone list:', error);
    }
  };

  const formatGMTOffset = (offsetSeconds: number) => {
    const hours = Math.floor(Math.abs(offsetSeconds) / 3600);
    const minutes = Math.floor((Math.abs(offsetSeconds) % 3600) / 60);
    const sign = offsetSeconds >= 0 ? '+' : '-';
    return `GMT${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const handleLoad = (autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      console.log('🛐 place:', place);
      if (place && place.formatted_address && place.geometry) {
        const address = place.formatted_address;
        onChange(address); // update the address field
        console.log('🛐 address:', address);
        const location = place.geometry?.location;
        if (location) {
          fetchTimezoneAndUpdateField(location.lat(), location.lng());
          console.log('🛐 locationLocation:', location.lat(), location.lng());
        } else {
          console.warn('Location is undefined');
        }
      } else {
        console.warn('Place does not have a formatted address or geometry');
      }
    }
  };

  const fetchTimezoneAndUpdateField = async (lat: number, lng: number) => {
    setLoading(true);
    console.log('🛐 Fetching timezone for:', lat, lng);
    try {
      const apiKey = 'N2IE9BKU2EQR'; // Your TimeZoneDB API key
      const url = `https://api.timezonedb.com/v2.1/get-time-zone?key=${apiKey}&format=json&by=position&lat=${lat}&lng=${lng}`;

      const response = await fetch(url);
      const data = await response.json();

      console.log('🛐 data:', data);
      console.log('🛐 data.status:', data.status);

      if (data.status === 'OK') {
        console.log('Fetched Timezone:', data.zoneName);
        // Just pass the raw timezone ID directly
        onTimezoneSelect(data.zoneName);
      } else {
        console.error('Error in TimeZoneDB data:', data.message);
      }
    } catch (error) {
      console.error('Error fetching timezone:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Autocomplete onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}>
        <TextInput
          label="Address"
          name="Address"
          placeholder="Event Address"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          labelProps={{
            style: {
              display: 'block',
              textAlign: 'left',
              marginBottom: '5px',
              color: 'white',
            },
          }}
          styles={{ label: { color: '#FFF7ED' } }}
        />
      </Autocomplete>

      <Select
        label="Timezone"
        placeholder="Select timezone"
        name="Timezone"
        data={timezoneOptions}
        value={timezoneValue}
        onChange={(value) => value && onTimezoneSelect(value)}
        searchable
        labelProps={{
          style: {
            display: 'block',
            textAlign: 'left',
            marginBottom: '5px',
            color: 'white',
          },
        }}
        disabled={loading}
        styles={{ label: { color: '#FFF7ED' } }}
      />
    </>
  );
};

export default AddressAutocomplete;
